/**
 * Shared match start-time formatter for all Sportsnew/live/inplay displays.
 *
 * Backend sends startsAt as a UTC ISO string and the DB/raw fixture-results
 * both convert to KST correctly — the one-hour mismatch users saw came from
 * formatting with `new Date(value).getHours()/getMinutes()`, which reads the
 * date in the BROWSER's local timezone instead of a fixed one. That made the
 * displayed time drift by the user's own timezone/DST offset.
 *
 * Always render in a fixed Asia/Seoul timezone via Intl.DateTimeFormat,
 * regardless of the viewer's machine/browser locale or timezone.
 */
export const formatSportsMatchTimeKst = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    // Per the Intl spec, `hour12` (when present) overrides `hourCycle` — so
    // setting both `hour12: false` AND `hourCycle: 'h23'` silently discards
    // the hourCycle and falls back to ko-KR's locale-default cycle, which
    // renders midnight as "24:30" instead of "00:30". Set ONLY hourCycle so
    // it's actually honored, pinning midnight to 0 rather than 24.
    hourCycle: 'h23',
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
};

/** Pull whichever start-time field a fixture/match object carries. */
export const getSportsMatchStartValue = (obj) =>
  obj?.startsAt ?? obj?.start_time ?? obj?.scheduled ?? obj?.startTime ?? null;

/** Convenience: resolve the field AND format it in one call. */
export const formatSportsMatchTime = (obj) => formatSportsMatchTimeKst(getSportsMatchStartValue(obj));

/**
 * Prematch list safety guard: if a fixture's scheduled start has elapsed,
 * stale main-odds snapshots must render locked until the list composition
 * catches up through REST/WS.
 */
export const hasSportsMatchStartElapsed = (obj, nowMs = Date.now()) => {
  const startValue = getSportsMatchStartValue(obj);
  if (!startValue) return false;
  const startMs = new Date(startValue).getTime();
  if (!Number.isFinite(startMs)) return false;
  return startMs <= nowMs;
};
